import Swiper from 'swiper';
import { Pagination, EffectFade, Controller } from 'swiper/modules';
import WindowSize from '../singleton/WindowSize';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
class HomeCarousel {
  $container: JQuery<HTMLElement>;
  $inner: JQuery<HTMLElement>;
  $images: JQuery<HTMLElement>;
  $content: JQuery<HTMLElement>;
  $btns: JQuery<HTMLElement>;
  imagesSwiper!: Swiper | null;
  contentSwiper!: Swiper | null;
  isHeroFeatures: boolean = false;
  windowSize: WindowSize;
  autoplayTimer: number | null;
  isDesktop: boolean;
  isInView: boolean;
  viewportObserver: IntersectionObserver | null;

  constructor(el: HTMLElement | JQuery<HTMLElement>) {
    this.$container = $(el);
    this.$inner = this.$container.find('.home-carousel__inner');
    this.$images = this.$container.find('.home-carousel__images');
    this.$content = this.$container.find('.home-carousel__content');
    this.$btns = this.$container.find('.home-carousel__btn');
    this.windowSize = WindowSize.getInstance();
    this.autoplayTimer = null;
    this.isDesktop = this.windowSize.getSize().width >= 1024;
    this.isInView = false;
    this.viewportObserver = null;

    this.isHeroFeatures = this.$container.hasClass('hero-features');

    this.init();
  }

  init() {
    this.initSwipers();

    this.$btns.on('click', this.onBtnClick.bind(this));
    this.windowSize.addListener(this.onResize.bind(this));

    if (this.isHeroFeatures) {
      this.initViewportObserver();
    }
  }

  onResize() {
    this.isDesktop = this.windowSize.getSize().width >= 1024;
    this.updateAutoplayState();
  }

  initViewportObserver() {
    if (this.viewportObserver) return;
    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.isInView = Boolean(entry && entry.isIntersecting);
        this.updateAutoplayState();
      },
      {
        threshold: 0.2,
      },
    );

    this.viewportObserver.observe(this.$container[0]);
  }

  updateAutoplayState() {
    if (this.isDesktop && this.isInView) {
      this.initAutoplay();
      return;
    }

    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  initAutoplay() {
    if (!this.isDesktop || !this.isInView) return;
    if (!this.imagesSwiper) return;
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
    }

    this.createTimer();
  }

  createTimer() {
    this.autoplayTimer = window.setInterval(() => {
      this.imagesSwiper?.slideNext();
    }, 4000);
  }


  onBtnClick(event: Event) {
    event.preventDefault();
    const target = $(event.currentTarget as HTMLElement);
    const index = target.index();

    this.imagesSwiper?.slideToLoop(index);

    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.createTimer();
    }
  }

  initSwipers() {
    this.initImagesCarousel();
    this.initContentCarousel();

    if (!this.imagesSwiper || !this.contentSwiper) return;

    this.imagesSwiper.controller.control = this.contentSwiper;
    this.contentSwiper.controller.control = this.imagesSwiper;
  }

  initImagesCarousel() {
    this.imagesSwiper = new Swiper(this.$images[0], {
      modules: [EffectFade, Controller],
      effect: 'fade',
      speed: 700,
      loop: true,
      on: {
        slideChange: this.onImagesSlideChange.bind(this),
      },
    });
  }

  onImagesSlideChange() {
    const realIndex = this.imagesSwiper?.realIndex;
    if (typeof realIndex !== 'number') return;
    const btnIndex = this.$btns.length ? realIndex % this.$btns.length : realIndex;
    this.$btns.eq(btnIndex).addClass('is-active');
    this.$btns.not(this.$btns.eq(btnIndex)).removeClass('is-active');
  }

  initContentCarousel() {
    this.contentSwiper = new Swiper(this.$content[0], {
      modules: [EffectFade, Controller, Pagination],
      speed: 700,
      loop: true,
      spaceBetween: 24,
      slideToClickedSlide: true,
      pagination: {
        el: this.$content.find('.swiper-pagination')[0],
        clickable: true,
      },
      breakpoints: {
        1024: {
          allowTouchMove: false,
        },
      },
    });
  }
}

export default HomeCarousel;
